import format from 'date-fns/format';
import endOfWeek from 'date-fns/endOfWeek';
import subWeeks from 'date-fns/subWeeks';
import formatRelative from 'date-fns/formatRelative';
import formatDistance from 'date-fns/formatDistance';
import formatDistanceToNowStrict from 'date-fns/formatDistanceStrict';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import differenceInMonths from 'date-fns/differenceInMonths';
import subMonths from 'date-fns/subMonths';
import subYears from 'date-fns/subYears';
import getTime from 'date-fns/getTime';
import endOfMonth from 'date-fns/endOfMonth';
import enGB from 'date-fns/locale/en-GB';
import parseISO from 'date-fns/parseISO';
import differenceInDays from 'date-fns/differenceInDays';
import sub from 'date-fns/sub';

export function dateFormatFromStr(
  date,
  formatStr = 'd MMM yyyy',
  locale = enGB,
  defaultValue = '-',
) {
  return dateFormat(new Date(date), formatStr, locale, defaultValue);
}

export function dateFormat(date, formatStr = 'd MMM yyyy', locale = enGB, defaultValue = '-') {
  try {
    return format(date, formatStr, { locale });
  } catch {
    return defaultValue;
  }
}

export function formatISODate(date, formatStr) {
  return format(parseISO(date), formatStr, { locale: enGB });
}

export function endOfWeekFromToday(numberOfWeeksBack = 1) {
  return endOfWeek(subWeeks(new Date(), numberOfWeeksBack, { locale: enGB }), { locale: enGB });
}

export const formatHours = (hours) => `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

export function formatMinutes(minutes) {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const minutesWithoutHours = Math.round(minutes % 60);

  if (minutesWithoutHours === 0) {
    return formatHours(hours);
  }

  const date = new Date(0, 0, 0, 0, minutes);

  return format(date, "K'h' mm'm'", { locale: enGB });
}

export function toRelativeFromNow(date, customMapping = {}) {
  const mapping = {
    today: "'Today'",
    yesterday: "'Yesterday'",
    tomorrow: "'Tomorrow'",
    nextWeek: 'EEEE',
    lastWeek: "'Last' EEEE",
    other: 'do MMM',
    ...customMapping,
  };

  const locale = {
    ...enGB,
    formatRelative: (token) => mapping[token],
  };
  return formatRelative(date, new Date(), { locale });
}

export function toDistanceFromNow(date, defaultValue = '-', options = {}) {
  return date ? formatDistance(date, new Date(), options) : defaultValue;
}

export function toDistanceFromNowISO(date) {
  return formatDistance(parseISO(date), new Date());
}

export function toDistanceFromNowISOStrict(date) {
  return formatDistanceToNowStrict(parseISO(date), new Date());
}

export function endWeeksBetweenDates(start, end) {
  if (!start || !end) {
    return [];
  }

  const startDate = endOfWeek(start, { locale: enGB });
  const endDate = endOfWeek(end, { locale: enGB });

  const numberOfWeeks = differenceInWeeks(endDate, startDate, { locale: enGB });

  if (numberOfWeeks <= 0) {
    return [];
  }

  return Array.from(Array(numberOfWeeks + 1)).map((_, index) =>
    subWeeks(endDate, numberOfWeeks - index, { locale: enGB }),
  );
}

export function endMonthsBetweenDates(start, end) {
  if (!start || !end) {
    return [];
  }

  const startDate = endOfMonth(start, { locale: enGB });
  const endDate = endOfMonth(end, { locale: enGB });

  const numberOfMonths = differenceInMonths(endDate, startDate, { locale: enGB });

  if (numberOfMonths <= 0) {
    return [];
  }

  return Array.from(Array(numberOfMonths + 1)).map((_, index) =>
    subMonths(endDate, numberOfMonths - index, { locale: enGB }),
  );
}

export function diffBetweenDatesInMonths(start, end) {
  return differenceInMonths(new Date(end), new Date(start));
}

export function expiry(endDate) {
  if (!endDate) return '';

  const today = new Date();
  const difference = differenceInMonths(endDate, today);
  const differenceTitle = difference === 1 ? 'Month' : 'Months';

  return difference < 12
    ? `${difference} ${differenceTitle}`
    : format(endDate, 'MMM yyyy', { awareOfUnicodeTokens: true });
}

export function isDate(value) {
  return value instanceof Date && !Number.isNaN(value.valueOf());
}

export const getPreviousYear = (date) => getTime(subYears(date * 1000, 1)) / 1000;

export function dateToTimestamp(date) {
  return isDate(date)
    ? // eslint-disable-next-line no-bitwise
      (date.getTime() / 1000) | 0
    : undefined;
}

export const lastUpdated = (objectData) =>
  objectData.reduce((latest, { updatedAt }) => {
    if (updatedAt > latest) {
      return updatedAt;
    }

    return latest;
  }, '');

export const getLast12Months = (startingFrom = new Date()) => {
  const months = [];

  for (let i = 0; i < 12; i++) {
    const auxDate = subMonths(startingFrom, i);

    months.push(auxDate);
  }

  return months.reverse();
};

export const isSameMonth = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
};

export const isEarlierThanToday = (dateValue) => {
  const date = new Date(dateValue);
  const isValid = date.getTime() === date.getTime();
  if (!isValid) return false;
  const today = new Date();
  return differenceInDays(today, date) > 0;
};

export const subtract = (period, date = new Date()) => {
  return sub(date, period);
};

export const getDateFromMonth = (month, formatStr = 'd MMM yyyy') => {
  const date = new Date();
  date.setMonth(+month - 1);

  return dateFormat(date, formatStr);
};
